
import { Memory } from "@/types/memory";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash, FolderOpen, Shield, FileText, MessageSquare } from "lucide-react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useEffect, useRef } from "react";

interface MemoryTableProps {
  memories: Memory[];
  isLoading: boolean;
  selectedMemories: number[];
  onSelectionChange: (selectedIds: number[]) => void;
  onDeleteMemory: (id: number) => void;
  onRecategorizeMemory: (memory: Memory) => void;
  onUpdateSecurityLevel: (memoryId: number, securityLevel: 'low' | 'medium' | 'high') => void;
  showFileName?: boolean;
}

const MemoryTable = ({ 
  memories, 
  isLoading, 
  selectedMemories, 
  onSelectionChange, 
  onDeleteMemory,
  onRecategorizeMemory,
  onUpdateSecurityLevel,
  showFileName = false
}: MemoryTableProps) => {
  const selectAllCheckboxRef = useRef<HTMLButtonElement>(null);

  const allSelected = memories.length > 0 && selectedMemories.length === memories.length;
  const someSelected = selectedMemories.length > 0 && selectedMemories.length < memories.length;

  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      const checkboxElement = selectAllCheckboxRef.current.querySelector('input[type="checkbox"]') as HTMLInputElement;
      if (checkboxElement) {
        checkboxElement.indeterminate = someSelected;
      }
    }
  }, [someSelected]);

  const handleSelectMemory = (memoryId: number, checked: boolean) => {
    if (checked) {
      onSelectionChange([...selectedMemories, memoryId]);
    } else {
      onSelectionChange(selectedMemories.filter(id => id !== memoryId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      onSelectionChange(memories.map(memory => memory.id));
    } else {
      onSelectionChange([]);
    }
  };

  const getSecurityLevelColor = (level: string | undefined) => {
    switch (level) {
      case 'low':
        return 'text-green-600';
      case 'medium':
        return 'text-yellow-600';
      case 'high':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  if (isLoading) {
    return <div className="text-center py-8 text-muted-foreground">Loading memories...</div>;
  }

  if (memories.length === 0) {
    return <div className="text-center py-8 text-muted-foreground">No memories found.</div>;
  }

  const renderMemoryItem = (memory: Memory) => (
    <div 
      key={memory.id} 
      className="bg-card border border-border rounded-2xl hover:shadow-lg transition-all duration-300 p-4 sm:p-5 backdrop-blur-sm"
    >
      {/* Mobile: Stack everything vertically */}
      <div className="space-y-3">
        {/* Header row with checkbox and content */}
        <div className="flex gap-3">
          <Checkbox 
            checked={selectedMemories.includes(memory.id)}
            onCheckedChange={(checked) => handleSelectMemory(memory.id, checked as boolean)}
            className="mt-1 flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-2">
              {memory.filename ? (
                <FileText className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
              ) : (
                <MessageSquare className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1 min-w-0">
                {memory.filename ? (
                  <div className="space-y-1">
                    <p className="text-foreground font-medium text-sm sm:text-base leading-tight break-words">
                      {memory.filename}
                    </p>
                    {memory.memory && (
                      <p className="text-muted-foreground text-xs sm:text-sm line-clamp-3 break-words">
                        {memory.memory}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-foreground font-medium text-sm sm:text-base leading-tight line-clamp-3 break-words">
                    {memory.memory}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile: Actions and metadata in a more organized layout */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 pt-3 border-t border-border/50">
          {/* Top row on mobile: Category and Security */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 flex-1">
            {/* Category */}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => onRecategorizeMemory(memory)}
              className="text-accent hover:text-accent/80 hover:bg-accent/10 h-7 px-2 flex items-center gap-1 text-xs border border-accent/20 rounded-lg"
              title="Re-categorize"
            >
              <FolderOpen className="h-3 w-3" />
              <span className="capitalize">
                {memory.category}
              </span>
            </Button>

            {/* Security Level */}
            <div className="flex items-center gap-1">
              <Shield className={`h-3 w-3 ${getSecurityLevelColor(memory.security_level || 'low')}`} />
              <Select
                value={memory.security_level || 'low'}
                onValueChange={(value: 'low' | 'medium' | 'high') => onUpdateSecurityLevel(memory.id, value)}
              >
                <SelectTrigger className={`h-7 w-16 sm:w-20 text-xs bg-input border-border p-1 ${getSecurityLevelColor(memory.security_level || 'low')} rounded-lg`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border backdrop-blur-sm">
                  <SelectItem value="low" className="text-green-600 hover:bg-muted focus:bg-muted">
                    Low
                  </SelectItem>
                  <SelectItem value="medium" className="text-yellow-600 hover:bg-muted focus:bg-muted">
                    Medium
                  </SelectItem>
                  <SelectItem value="high" className="text-red-600 hover:bg-muted focus:bg-muted">
                    High
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Bottom row on mobile: Date and Delete */}
          <div className="flex items-center justify-between sm:justify-end gap-3">
            <span className="text-muted-foreground text-xs sm:text-sm">
              {new Date(memory.created_at).toLocaleDateString()}
            </span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => onDeleteMemory(memory.id)}
              className="text-destructive hover:text-destructive/80 hover:bg-destructive/10 h-8 w-8 p-0 flex-shrink-0 border border-destructive/20 rounded-lg"
              title="Delete"
            >
              <Trash className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <TooltipProvider>
      <div className="space-y-3 sm:space-y-4">
        <div className="space-y-3 sm:space-y-2">
          {memories.map((memory) => renderMemoryItem(memory))}
        </div>

        {/* Select All and Bulk Actions at Bottom */}
        {memories.length > 0 && (
          <div className="flex items-center justify-between p-4 sm:p-5 bg-muted/30 rounded-2xl border border-border backdrop-blur-sm">
            <div className="flex items-center gap-3">
              <Checkbox 
                ref={selectAllCheckboxRef}
                checked={allSelected}
                onCheckedChange={handleSelectAll}
              />
              <span className="text-foreground font-medium text-sm">
                {selectedMemories.length > 0 
                  ? `${selectedMemories.length} selected` 
                  : `Select all ${memories.length} items`
                }
              </span>
            </div>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};

export default MemoryTable;
